import { $, bindClick } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { api } from "../api.js";

let currentStoreId = "";
let mapInstance = null;
let mapMarker = null;
let amapReady = null;

function notify(text, ok) {
  const el = $("key-banner");
  if (!el) return;
  el.textContent = text;
  el.classList.toggle("hidden", !text);
  el.classList.toggle("ok", !!ok);
}

function readNum(id) {
  const el = $(id);
  if (!el) return null;
  const v = parseFloat(String(el.value || "").trim());
  return Number.isFinite(v) ? v : null;
}

function writeStoreFields(store) {
  if (!store) return;
  currentStoreId = String(store.store_id || "").trim();
  if ($("mall-store-id")) $("mall-store-id").value = currentStoreId;
  if ($("mall-store-code")) $("mall-store-code").value = store.shop_code || "";
  if ($("mall-store-lng")) $("mall-store-lng").value = store.lng != null ? store.lng : "";
  if ($("mall-store-lat")) $("mall-store-lat").value = store.lat != null ? store.lat : "";
  if ($("mall-store-address")) $("mall-store-address").value = store.address_text || "";
  if ($("mall-store-city-code")) $("mall-store-city-code").value = store.city_code || "";
  if ($("mall-store-status")) $("mall-store-status").value = store.status || "pending";
}

async function loadAmapConfig() {
  const base = ($("base-url") && $("base-url").value.trim()) || window.location.origin;
  const res = await fetch(`${base.replace(/\/$/, "")}/api/v1/demo/config`);
  const data = await res.json().catch(() => ({}));
  return {
    jsKey: String(data.amap_js_key || "").trim(),
    security: String(data.amap_security_js_code || "").trim(),
  };
}

function loadAmapScript(jsKey, security) {
  if (window.AMap) return Promise.resolve(window.AMap);
  if (amapReady) return amapReady;
  amapReady = new Promise((resolve, reject) => {
    if (security) {
      window._AMapSecurityConfig = { securityJsCode: security };
    }
    const s = document.createElement("script");
    s.src = `https://webapi.amap.com/maps?v=2.0&key=${encodeURIComponent(jsKey)}`;
    s.onload = () => resolve(window.AMap);
    s.onerror = () => reject(new Error("高德地图脚本加载失败"));
    document.head.appendChild(s);
  });
  return amapReady;
}

function setPin(lng, lat) {
  if ($("mall-store-lng")) $("mall-store-lng").value = lng.toFixed(6);
  if ($("mall-store-lat")) $("mall-store-lat").value = lat.toFixed(6);
  if (mapInstance && window.AMap) {
    if (mapMarker) {
      mapMarker.setPosition([lng, lat]);
    } else {
      mapMarker = new window.AMap.Marker({ position: [lng, lat], map: mapInstance });
    }
    mapInstance.setCenter([lng, lat]);
  }
}

async function ensureMap() {
  const wrap = $("mall-store-map");
  if (!wrap) return false;
  wrap.classList.remove("hidden");
  const cfg = await loadAmapConfig();
  if (!cfg.jsKey) {
    notify("未配置 AMAP_JS_KEY，请手动填写 lng/lat", false);
    return false;
  }
  await loadAmapScript(cfg.jsKey, cfg.security);
  const lng = readNum("mall-store-lng") || 120.15507;
  const lat = readNum("mall-store-lat") || 30.27415;
  if (!mapInstance) {
    mapInstance = new window.AMap.Map("mall-store-map", {
      zoom: 14,
      center: [lng, lat],
    });
    mapInstance.on("click", (ev) => {
      const p = ev.lnglat;
      if (p) setPin(p.lng, p.lat);
    });
  }
  setPin(lng, lat);
  return true;
}

function renderListings(pool, selected) {
  const box = $("mall-store-listings");
  if (!box) return;
  const sel = new Set(selected || []);
  if (!pool || !pool.length) {
    box.innerHTML = "<p class='mc-desc'>商品池为空，请先登记/B1 下发。</p>";
    return;
  }
  const rows = pool
    .map(
      (num) =>
        `<label class="mc-checkline"><input type="checkbox" data-num-iid="${escHtml(num)}" ${
          sel.has(num) ? "checked" : ""
        } /> <code>${escHtml(num)}</code></label>`
    )
    .join("");
  box.innerHTML = `<div class="mc-checkgrid">${rows}</div>`;
}

export async function loadMallStorePanel() {
  const { ok, data } = await api("GET", "/api/v1/fitting/mall/stores");
  if (!ok || !data) {
    notify("实体店加载失败", false);
    return;
  }
  const stores = (data.items || data.stores || []).slice();
  const store = stores[0] || null;
  writeStoreFields(store);
  if (!currentStoreId) {
    renderListings([], []);
    return;
  }
  const poolRes = await api("GET", "/api/v1/fitting/mall/product-pool");
  const pool = (poolRes.ok && poolRes.data && poolRes.data.num_iids) || [];
  const listRes = await api(
    "GET",
    `/api/v1/fitting/mall/stores/${encodeURIComponent(currentStoreId)}/listings`
  );
  const listed = ((listRes.ok && listRes.data && listRes.data.items) || []).map(
    (r) => String(r.num_iid || "")
  );
  renderListings(pool, listed);
}

async function saveStore() {
  const body = {
    store_id: ($("mall-store-id") && $("mall-store-id").value.trim()) || undefined,
    lng: readNum("mall-store-lng"),
    lat: readNum("mall-store-lat"),
    address_text: ($("mall-store-address") && $("mall-store-address").value.trim()) || null,
    status: ($("mall-store-status") && $("mall-store-status").value) || "pending",
  };
  if (body.lng == null || body.lat == null) {
    notify("激活前须在地图标定 lng/lat", false);
    return;
  }
  const { ok, data } = await api("PUT", "/api/v1/fitting/mall/stores", { body });
  if (!ok) {
    notify((data && data.message) || "保存失败", false);
    return;
  }
  writeStoreFields(data);
  notify("实体店已保存", true);
  await loadMallStorePanel();
}

async function saveListings() {
  if (!currentStoreId) {
    notify("请先保存实体店", false);
    return;
  }
  const box = $("mall-store-listings");
  const nums = [];
  if (box) {
    box.querySelectorAll("input[data-num-iid]:checked").forEach((el) => {
      const n = el.getAttribute("data-num-iid");
      if (n) nums.push(n);
    });
  }
  const { ok, data } = await api(
    "PUT",
    `/api/v1/fitting/mall/stores/${encodeURIComponent(currentStoreId)}/listings`,
    { body: { num_iids: nums } }
  );
  if (!ok) {
    notify((data && data.message) || "授权清单保存失败", false);
    return;
  }
  notify(`已保存 ${nums.length} 款 listing`, true);
  await loadMallStorePanel();
}

export function initMallStoreHandlers() {
  bindClick("btn-mall-store-save", () => saveStore());
  bindClick("btn-mall-store-refresh", () => loadMallStorePanel());
  bindClick("btn-mall-store-listings-save", () => saveListings());
  bindClick("btn-mall-store-map", async () => {
    try {
      const ok = await ensureMap();
      if (ok) notify("点击地图放置坐标", true);
    } catch (err) {
      notify((err && err.message) || "地图初始化失败", false);
    }
  });
}
